import {DateTime, Interval} from "luxon";
import {useGetKpiValueQuery, useListKpisQuery, useListKpiTargetsQuery, useListKpiValuesQuery} from "../apis/kpiApi";
import {useMemo, useCallback} from "react";
import {useListCompanyAccountsQuery} from "../apis/companiesApi";
import {useUserProfileQuery} from "../apis/authenticationApi";
import {useSelector} from "react-redux";
import { useResponsibleHiearchicalList } from "./taskHooks";

const sum = (values) => {
    return (values || []).filter(val => val)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
}

const average = (values) => {
    const results = (values || [])
        .filter(val => val);

    if(!results.length) return 0;

    return results[results.length - 1];
}

const calculateAnnualTotalTarget = (target) => sum((target?.targets || []));

const calculateAverageTarget = (target) => (target?.targets || [])[3] || 0;

const AnnualCalculations = {
    Benchmark: calculateAverageTarget,
    Target: calculateAnnualTotalTarget
}

const calculateCumulativeTotalActualByQuarter = (kpiValue, quarter) => {
    return sum((kpiValue?.values || [])[quarter] || []);
}

const calculateAveragePerQuarterActualByQuarter = (kpiValue, quarter) => {
    return average((kpiValue?.values || [])[quarter] || []);
}

const PerQuarterCalculations = {
    Target: calculateCumulativeTotalActualByQuarter,
    Benchmark: calculateAveragePerQuarterActualByQuarter
}

const calculateCumulativeTotalActual = (kpiValue, _) => {
    return sum((kpiValue?.values || []).flat());
}

const calculateAveragePerQuarterActual = (kpiValue, year) => {
    return (calculateAveragePerQuarterActualByQuarter(kpiValue, year < DateTime.now().year ? 3 : year > DateTime.now().year ? 0 : DateTime.now().quarter - 1));
}

const ActualCalculations = {
    Target: calculateCumulativeTotalActual,
    Benchmark: calculateAveragePerQuarterActual
}

const calculateCumulativeTotalTarget = (target) => {
    const currentYear = new Date().getFullYear();

    if(currentYear < target.year) return 0;
    if(currentYear > target.year) return calculateAnnualTotalTarget(target);

    const currentQuarter = DateTime.now().quarter - 1;
    const weeksIntoCurrentQuarter = Interval.fromDateTimes(DateTime.now().startOf('quarter'), DateTime.now()).length('weeks');
    const weeksInCurrentQuarter = Interval.fromDateTimes(DateTime.now().startOf('quarter'), DateTime.now().endOf('quarter')).length('weeks');

    return (target?.targets || []).reduce((previousValue, currentValue, currentIndex) => {
        if(currentIndex < currentQuarter) return previousValue + currentValue;
        else if(currentIndex === currentQuarter) return previousValue + (weeksIntoCurrentQuarter / weeksInCurrentQuarter) * currentValue;
        else return previousValue;
    }, 0);
}

const calculateAveragePerQuarterTarget = (target) => {
    const currentYear = new Date().getFullYear();
    const currentQuarter = currentYear < target.year ? 0 :
        currentYear > target.year ? 3 : DateTime.now().quarter - 1;

    return (target?.targets || [])[currentQuarter];
}

const TargetCalculations = {
    Target: calculateCumulativeTotalTarget,
    Benchmark: calculateAveragePerQuarterTarget
}

const formatDollarAmount = (val) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
}).format(val || 0);

const formatPercent = (val) => (val || 0).toFixed(2) + "%";
const formatCount = (val) => new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
}).format(val || 0);
const formatDollarAmountCondensed = (val) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
}).format(val || 0);

const formatPercentCondensed = (val) => (val || 0).toFixed(1) + "%";

const ValueTypeFormatters = {
    DollarAmount: formatDollarAmount,
    Percent: formatPercent,
    Number: formatCount
}

const ValueTypeCondensedFormatters = {
    DollarAmount: formatDollarAmountCondensed,
    Percent: formatPercentCondensed,
    Number: formatCount
}

export const formatTargetValue = (target, value, defaultValue, showQuarter) => {
    if(!value || !target.valueType || !ValueTypeCondensedFormatters[target.valueType]) {
        return defaultValue;
    }

    return ValueTypeCondensedFormatters[target.valueType](value) + (showQuarter && target.timeFrame === "Quarter" ? ` (${target.quarter})` : "");
}

export const useTargetAnnualGoal = (target) => {
    return useMemo(() => {
        if(!target || !target.type || !target.valueType || !AnnualCalculations[target.type])
            return "";

        const value = AnnualCalculations[target.type](target);

        if(!ValueTypeFormatters[target.valueType])
            return "";

        return ValueTypeFormatters[target.valueType](value);
    }, [target]);
}

export const useTargetYTDActualUnformatted = (target) => {
    const {data, isLoading} = useGetKpiValueQuery(target?.id);

    return useMemo(() => {
        if(!isLoading) {
            if(!target || !target.type || !ActualCalculations[target.type])
                return 0;

            return ActualCalculations[target.type](data, target.year);
        }
    }, [data, isLoading, target]);
}

export const useTargetYTDActual = (target) => {
    const {data, isLoading} = useGetKpiValueQuery(target?.id);

    return useMemo(() => {
        if(!isLoading) {
            if(!target || !target.type || !target.valueType || !AnnualCalculations[target.type])
                return "";

            const value = ActualCalculations[target.type](data, target.year);

            if(!ValueTypeCondensedFormatters[target.valueType])
                return "";

            return ValueTypeCondensedFormatters[target.valueType](value);
        }
    }, [data, isLoading, target]);
}

export const calculateTargetQuarterActual = (target, value, quarter) => {
    if(target.timeFrame === 'Quarter' && !compareQuarter(target, quarter)) {
        return "NA";
    }

    if(!target || !target.type || !PerQuarterCalculations[target.type])
        return "";

    return PerQuarterCalculations[target.type](value, quarter);
}

export const useBSI = () => {
    const {data} = useListKpiTargetsQuery();
    const {data: values} = useListKpiValuesQuery();
    const totalWeight = useKpiTotalScoreboardWeight();
    const totalScorecardCount = useKpiScorecardTargetCount();
    const selectedYear = useSelector(state => state.kpi.selectedYear);

    return (data || [])
        .filter(kpi => kpi.scorecard && kpi.year === selectedYear && (kpi.timeFrame === 'Annual') && (kpi.type === 'Benchmark' || kpi.type === 'Target'))
        .map(target => {
            const targetValue = TargetCalculations[target.type](target);

            if(targetValue === 0) return 0;

            let weight;
            const kpiValue = (values || []).find(val => val.target === target.id);

            if(!kpiValue) return 0;

            const ytdActualUnformatted = ActualCalculations[target.type](kpiValue, target.year);

            if(totalWeight === 0)
            {
                weight = 1.0 / totalScorecardCount;
            }
            else
            {
                weight = target.weight / totalWeight;
            }

            if(target.improvementDirection === 'Higher')
                return (((Math.min(ytdActualUnformatted, targetValue) / targetValue) * weight) * 100.0);
            else
                return ((targetValue / (Math.max(ytdActualUnformatted, targetValue)) * weight) * 100.0);
        })
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0).toFixed(1) || "0.0";
}


export const useKpiBSI = (target) => {
    const ytdActualUnformatted = useTargetYTDActualUnformatted(target);
    const totalWeight = useKpiTotalScoreboardWeight();
    const totalScorecardCount = useKpiScorecardTargetCount();

    if(!target.scorecard || target.targetValue === 0) return 0;

    let weight;

    if(totalWeight === 0)
    {
        weight = 1.0 / totalScorecardCount;
    }
    else
    {
        weight = target.weight / totalWeight;
    }

    return (((ytdActualUnformatted / target.targetValue) * weight) * 100.0).toFixed(1);
}

export const useKpiScorecardTargetCount = () => {
    const {data} = useListKpiTargetsQuery();
    const selectedYear = useSelector(state => state.kpi.selectedYear);

    return useMemo(() => (data || [])
        .filter(kpi => kpi.scorecard && kpi.year === selectedYear && (kpi.timeFrame === 'Annual') && (kpi.type === 'Benchmark' || kpi.type === 'Target'))
        .length, [data, selectedYear]);
}

export const useKpiTotalScoreboardWeight = () => {
    const {data} = useListKpiTargetsQuery();
    const selectedYear = useSelector(state => state.kpi.selectedYear);
    return useMemo(() => (data || [])
        .filter(kpi => kpi.scorecard && kpi.year === selectedYear && (kpi.timeFrame === 'Annual') && (kpi.type === 'Benchmark' || kpi.type === 'Target'))
        .reduce((previousValue, currentValue) => previousValue + currentValue.weight, 0) || 0, [data, selectedYear]);
}

export const useTargetQuarterActual = (target, quarter) => {
    const {data, isLoading} = useGetKpiValueQuery(target?.id);

    return useMemo(() => {
        if(!isLoading) {
            if(target.timeFrame === 'Quarter' && !compareQuarter(target, quarter)) {
                return "NA";
            }

            if(!target || !target.type || !target.valueType || !PerQuarterCalculations[target.type])
                return "";

            const value = PerQuarterCalculations[target.type](data, quarter);

            if(!ValueTypeFormatters[target.valueType])
                return "";

            return ValueTypeFormatters[target.valueType](value);
        }
    }, [data, isLoading, quarter, target]);
}

const compareQuarter = (target, quarter) => {
    if(target.quarter === "Q1" && quarter === 0) return true;
    if(target.quarter === "Q2" && quarter === 1) return true;
    if(target.quarter === "Q3" && quarter === 2) return true;
    if(target.quarter === "Q4" && quarter === 3) return true;

    return false;
}

export const getTargetQuarter = (target) => {
    if(target?.timeFrame !== 'Quarter') return -1;

    if(target?.quarter === "Q1") return 0;
    if(target?.quarter === "Q2") return 1;
    if(target?.quarter === "Q3") return 2;
    if(target?.quarter === "Q4") return 3;

    return -1;
}

export const useTargetQuarterGoal = (target, quarter) => {
    return useMemo(() => {
        if(target.timeFrame === 'Quarter' && !compareQuarter(target, quarter)) {
            return "NA";
        }

        if(!target || !target.valueType || !ValueTypeFormatters[target.valueType])
            return "";

        return ValueTypeFormatters[target.valueType](target.targets[quarter]);
    }, [target, quarter]);
}

export const useTargetYTDGoalUnformatted = (target) => {
    return useMemo(() => {
        if(!target || !target.type || !TargetCalculations[target.type])
            return 0;

        return TargetCalculations[target.type](target);
    }, [target]);
}

export const useTargetYTDGoal = (target) => {
    return useMemo(() => {
        return calculateTargetYTDGoal(target);
    }, [target]);
}

export const calculateTargetYTDGoal = (target) => {
    if(!target || !target.type || !TargetCalculations[target.type])
        return "";

    const value = TargetCalculations[target.type](target);

    if(!TargetCalculations[target.type])
        return "";

    return ValueTypeCondensedFormatters[target.valueType](value);
}


export const useGetKPI = (kpiName) => {
    const {data, isLoading} = useListKpisQuery();

    return !isLoading ? data.find(kpi => kpi.kpi === kpiName) : undefined;
}

export const useTargetKPIName = (target) => {
    const {data, isLoading} = useListKpisQuery();

    return !isLoading ? data.find(kpi => kpi.kpi === target?.kpi)?.name : "";
}

export const useTargetKPIResponsible = (target) => {
    const {data, isLoading} = useListCompanyAccountsQuery();

    if(isLoading) return "";

    const responsible = data.find(user => user.id === target?.responsible);

    if(!responsible) {
        return "-";
    }

    return responsible?.registered ? (responsible?.firstName + " " + responsible?.lastName) : responsible?.email;
}

export const useIsCurrentUserResponsible = (target) => {
    const {data, isLoading} = useUserProfileQuery();

    if(isLoading) return false;

    return data?.company?.role === 'CompanyAdmin' || data?.company?.role === 'CompanyOwner' ||
        (target?.responsible && target?.responsible === data?.company?.account);
}



export const useIsParentKpiInHiearchy = () => {
    const {data: kpiTargets} = useListKpiTargetsQuery();
    
    const parentKpiInHiearchy  = (target, kpiId, visited) => {
        if(!target?.parent || !kpiId) {
            return false;
        } else if(target.parent === kpiId) {
            return true;
        } else {
            if(visited.includes(target.id)) { //this is only to prevent loops if some issue in the DB
                return true;
            } else {
                visited.push(target.id);
                return parentKpiInHiearchy((kpiTargets || []).find(e => e.id === target.parent), kpiId, visited);
            }
        }
    }
    return useCallback((target, kpiId) => {
        return parentKpiInHiearchy(target, kpiId, []);
    }); 
}



export const useTeamKpis = (userId) => {
    const {data: kpiTargets} = useListKpiTargetsQuery();
    const {data: userProfile} = useUserProfileQuery();

    const hierarchyUsers = useResponsibleHiearchicalList(userId ?? userProfile.company.account)?.map(e => e.id);

    return useMemo(() => {
        return (kpiTargets || []).filter(e => (hierarchyUsers || []).includes(e.responsible));
        
    }, [kpiTargets, hierarchyUsers, userId, userProfile]);

}